import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import { forceTokenRefresh } from '@/lib/logto';
import { recheckLogtoRoles } from '@/lib/roleRevalidation';

/**
 * Cosa si vede quando manca il ruolo — al posto di NIENTE.
 *
 * 🔴 IL DIFETTO CHE CHIUDE [M or45, 2026-09-06 su `stg-tma`]: `<RoleGate>` aveva
 * `fallback = null` per difetto e nessuno dei suoi usi passava un fallback. Un utente a cui
 * il ruolo `beta` era appena stato assegnato apriva la sezione e vedeva **una schermata
 * vuota** — `main` con `innerText` vuoto. Non «non puoi entrare»: proprio niente, che a
 * schermo e' indistinguibile da un guasto della app, e manda a cercare un problema dove non
 * c'e'. E' la stessa famiglia del vuoto affermato che questa base di codice ha gia' pagato
 * due volte oggi (la lista incontri, il degrado delle schermate Limulo).
 *
 * ⚠️ **Il bottone non e' decorativo e non ricarica la pagina.** `recheckLogtoRoles()` fa
 * rileggere il claim `roles` dal token corrente: se nel frattempo il token e' stato
 * rinnovato con il ruolo dentro, la sezione compare senza che l'utente debba sapere che
 * esiste una cosa chiamata token. Se il ruolo non c'e' ancora, resta questo pannello — ed
 * e' per questo che il bottone lascia un segno («ho guardato adesso»): un bottone che
 * sembra non fare nulla viene premuto dieci volte.
 */
export function RoleRequired({ role }: { role: string }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [ricontrollato, setRicontrollato] = useState(false);
  const [inCorso, setInCorso] = useState(false);

  return (
    <div className="px-4 pb-6 pt-5">
      <div className="rounded-xl border border-border bg-bg-secondary p-4">
        <h2 className="text-base font-semibold text-fg">{t('roleGate.title', { role })}</h2>
        <p className="mt-1 text-sm text-fg-muted">{t('roleGate.body')}</p>
        <Button
          data-tour="role-required-recheck"
          variant="secondary"
          className="mt-4 px-3 py-2 text-xs"
          disabled={inCorso}
          onClick={async () => {
            haptics.impact('light');
            setInCorso(true);
            // I-821: un refresh VERO del token (Logto rimette `roles` nel token solo quando lo
            // rinnova), poi la rilettura. Se il refresh fallisce (rete, grant scaduto) si legge il
            // token di prima, come prima: `forceTokenRefresh` non lancia mai.
            await forceTokenRefresh();
            recheckLogtoRoles();
            setInCorso(false);
            setRicontrollato(true);
          }}
        >
          {t('roleGate.recheck')}
        </Button>
        {/* Il segno che il tap ha fatto qualcosa. Se il ruolo fosse arrivato, questo
            pannello non sarebbe piu' a schermo: il fatto stesso che si legga questa riga
            e' l'esito, e dirlo evita il decimo tap. */}
        {ricontrollato ? (
          <p className="mt-3 text-xs text-fg-muted">{t('roleGate.stillMissing')}</p>
        ) : null}
      </div>
    </div>
  );
}
